import { FC, useState, useRef, useEffect } from "react";
import styles from "./Chat.module.scss";
import sendIcon from "../../images/icons/send.svg";
import emojiIcon from "../../images/icons/smiley.svg";
import EmojiPicker from "emoji-picker-react";
import { useMessages } from "./Services/useMessages";
import { FileUploaderChat } from "./FileUploaderChat/FileUploaderChat";

interface MessageFormProps {
  sendMessage: (e: React.FormEvent) => void;
  text: string;
  setText: (value: string) => void;
  interlocutor: number;
  isSending: boolean;
  isConnected: boolean;
  isDisabled: boolean;
  setSelectedFile: (file: File | null) => void;
  selectedFile: File | null;
}

export const MessageForm: FC<MessageFormProps> = ({
  sendMessage,
  text,
  setText,
  interlocutor,
  isSending,
  isConnected,
  setSelectedFile,
  selectedFile,
  isDisabled = false,
}) => {
  const [showEmoji, setShowEmoji] = useState(false);
  const { setTyping } = useMessages();
  const emojiRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "40px";
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height =
        scrollHeight <= 120 ? `${scrollHeight}px` : "120px";
    }
  }, [text]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiRef.current &&
        !emojiRef.current.contains(event.target as Node)
      ) {
        setShowEmoji(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle emoji selection
  const handleEmojiClick = (emojiObject: { emoji: string }) => {
    setText(text + emojiObject.emoji);
    textareaRef.current?.focus();
  };

  const toggleEmojiPicker = () => {
    setShowEmoji((prev) => !prev);
  };

  // Handle text changes and notify about typing status
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);
    setTyping(interlocutor, newText.length > 0);
  };

  // Handle Enter key to send message
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  return (
    <div className={styles.formContainer}>
      {!isConnected && (
        <div className={styles.connectionWarning}>
          <span className={styles.connectionDot}></span>
          <span>Waiting for connection...</span>
        </div>
      )}
      <FileUploaderChat
        onFileSelect={setSelectedFile}
        selectedFile={selectedFile}
        disabled={!isConnected || isDisabled}
        acceptedFileTypes="image/*"
      />

      <form
        className={`${styles.form} ${!isConnected ? styles.formDisabled : ""}`}
        onSubmit={sendMessage}
      >
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          placeholder={
            isDisabled
              ? "Chat is blocked."
              : selectedFile !== null
                ? "Please send the file first before typing a message"
                : "Type your message..."
          }
          className={styles.formInput}
          disabled={!isConnected || isSending || isDisabled}
        />

        <div className={styles.formActions}>
          <div ref={emojiRef} className={styles.emojiContainer}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={toggleEmojiPicker}
              title="Emoji"
              disabled={!isConnected || isSending || isDisabled}
            >
              <img src={emojiIcon} alt="Emoji" className={styles.iconMessage} />
            </button>

            {showEmoji && (
              <div className={styles.emojiPickerWrapper}>
                <EmojiPicker onEmojiClick={handleEmojiClick} />
              </div>
            )}
          </div>

          <button
            className={`${styles.sendButton} ${text.trim() || selectedFile !== null ? styles.active : ""}`}
            type="submit"
            disabled={
              !isConnected ||
              isSending ||
              (!text.trim() && selectedFile === null) ||
              isDisabled
            }
            title={!isConnected ? "Connecting to server..." : "Send message"}
          >
            <img src={sendIcon} alt="Send" className={styles.iconMessage} />
          </button>
        </div>
      </form>
    </div>
  );
};
